import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ticket, ChevronDown, Quote } from "lucide-react";
import { api, imageUrl } from "../lib/api";
import LudusWidget from "../components/LudusWidget";
import { PhotoFilmstrip, PhotoMosaic, PhotoWatermark } from "../components/PhotoElements";
import { SHOW_PHOTOS } from "../lib/photos";

export default function Home() {
  const [content, setContent] = useState(null);
  const [slide, setSlide] = useState(0);
  const [gallery, setGallery] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/home-content").then((r) => setContent(r.data)).catch(() => setContent(null));
    api.get("/gallery").then((r) => setGallery(r.data.slice(0, 9))).catch(() => {});
    api.get("/articles").then((r) => setArticles(r.data.slice(0, 4))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!content?.hero?.slides?.length) return;
    const total = content.hero.slides.length;
    const t = setInterval(() => setSlide((s) => (s + 1) % total), 6500);
    return () => clearInterval(t);
  }, [content]);

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center font-overline text-sm text-stone-400">
        Loading…
      </div>
    );
  }

  const { hero, quote, letter, snapshots, address_band: addressBand, articles: articlesSection } = content;
  const slides = hero?.slides || [];
  const current = slides[slide] || slides[0] || {};
  const isExternal = (url) => /^https?:\/\//i.test(url || "");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-screen min-h-[700px] overflow-hidden">
        {slides.map((s, i) => (
          <div
            key={i}
            className={`absolute inset-0 hero-slide ${i === slide ? "is-active" : "is-hidden"}`}
            style={{
              backgroundImage: `url(${imageUrl(s.image_url)})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-stone-950" />
        <div className="absolute inset-0 grain" />

        <div className="relative h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-32 md:pb-40">
          <p data-testid="hero-overline" className="font-overline text-xs md:text-sm text-[#DE9E48] mb-6">
            — {current.overline}
          </p>
          <h1 data-testid="hero-title" className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.95] tracking-tighter max-w-5xl">
            {current.title_top}
            <br />
            <span className="italic text-[#DE9E48]">{current.title_italic}</span>
          </h1>
          <p data-testid="hero-sub" className="font-body text-stone-300 text-lg md:text-xl max-w-xl mt-8 leading-relaxed">
            {current.sub}
          </p>
          <div className="flex flex-wrap gap-4 mt-10">
            {current.cta_url && (
              isExternal(current.cta_url) ? (
                <a
                  href={current.cta_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold"
                  data-testid="hero-cta"
                >
                  {current.cta_label} <Ticket size={14} />
                </a>
              ) : (
                <Link to={current.cta_url} className="btn-gold" data-testid="hero-cta">
                  {current.cta_label} <ArrowRight size={14} />
                </Link>
              )
            )}
            <Link to="/gallery" className="btn-outline" data-testid="hero-secondary">
              Encore Moments
            </Link>
          </div>

          <div className="flex gap-3 mt-16">
            {slides.map((_, i) => (
              <button
                key={i}
                data-testid={`hero-indicator-${i}`}
                onClick={() => setSlide(i)}
                className={`h-[2px] transition-all ${i === slide ? "w-12 bg-[#DE9E48]" : "w-6 bg-white/30"}`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-stone-400 flex flex-col items-center gap-2 animate-bounce">
          <span className="font-overline text-[0.65rem]">Scroll Down</span>
          <ChevronDown size={18} />
        </div>
      </section>

      {/* QUOTE */}
      <section className="py-32 px-6 md:px-10 spotlight relative">
        <div className="max-w-4xl mx-auto text-center">
          <Quote size={42} className="text-[#DE9E48] mx-auto mb-8 opacity-60" />
          <blockquote data-testid="paul-robeson-quote" className="font-display text-3xl md:text-5xl italic leading-tight text-[#FDFBF7]">
            &ldquo;{quote.text}&rdquo;
          </blockquote>
          {quote.attribution && (
            <p className="font-overline text-xs text-stone-400 mt-8">— {quote.attribution}</p>
          )}
          <div className="marquee mt-12 w-32 mx-auto" />
          {quote.body && (
            <p className="font-body text-stone-300 mt-10 leading-relaxed max-w-2xl mx-auto text-lg">
              {quote.body}
            </p>
          )}
        </div>
      </section>

      {/* PHOTO FILMSTRIP — a pulse of past-show energy bridging quote to tickets */}
      <PhotoFilmstrip tone="duotone" speed={80} />

      {/* UPCOMING EVENTS — Ludus widget */}
      <LudusWidget />

      {/* LETTER FROM THE ARTISTIC DIRECTOR */}
      <section className="py-28 md:py-36 px-6 md:px-10 relative overflow-hidden">
        <PhotoWatermark index={9} position="bottom-left" opacity={0.14} />
        <div
          aria-hidden="true"
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[120%] h-[80%] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(176,94,109,0.10) 0%, rgba(176,94,109,0.02) 40%, transparent 70%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto">
          {letter.overline && (
            <p className="font-overline text-xs text-[#DE9E48] mb-6 text-center tracking-[0.25em]">
              — {letter.overline}
            </p>
          )}
          <h2 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95] text-center mb-4">
            {letter.title_top} <br className="hidden md:block" />
            <span className="italic text-[#DE9E48]">{letter.title_italic}</span>
            {letter.title_after}
          </h2>
          {letter.greeting && (
            <p className="font-script text-2xl md:text-3xl text-stone-400 text-center mb-12">
              {letter.greeting}
            </p>
          )}

          {letter.director_image_url && (
            <figure className="md:float-right md:ml-10 md:mb-4 md:w-56 mb-10 max-w-[14rem] mx-auto md:mx-0 md:clear-both">
              <div className="relative">
                <img
                  src={imageUrl(letter.director_image_url)}
                  alt={letter.director_name || "Artistic Director"}
                  className="w-full grayscale-[15%] hover:grayscale-0 transition-all duration-700 border border-[#DE9E48]/30"
                  data-testid="director-image"
                  loading="lazy"
                />
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-16 h-5 bg-[#DE9E48]/30 backdrop-blur-sm rotate-[-2deg]" />
              </div>
              <figcaption className="mt-4 border-l-2 border-[#DE9E48] pl-3">
                <p className="font-display text-xl tracking-tight">{letter.director_name}</p>
                <p className="font-overline text-[0.65rem] text-stone-400 mt-0.5">
                  {letter.director_title}
                </p>
              </figcaption>
            </figure>
          )}

          <div data-testid="director-letter" className="font-body text-stone-300 leading-[1.85] text-base md:text-[1.075rem]">
            {(letter.paragraphs || []).map((p, idx) => {
              // Insert pulled quote between paragraphs 2 and 3
              const showPullQuote =
                idx === 2 && letter.pulled_quote && letter.paragraphs.length > 2;
              return (
                <React.Fragment key={idx}>
                  {showPullQuote && (
                    <blockquote className="my-14 md:clear-right">
                      <div className="marquee w-24 mb-6" />
                      <p className="font-display italic text-3xl md:text-4xl leading-[1.15] text-[#DE9E48] tracking-tight">
                        &ldquo;{letter.pulled_quote}&rdquo;
                      </p>
                      <div className="marquee w-24 mt-6" />
                    </blockquote>
                  )}
                  <p className={idx === 0 ? "drop-cap" : "mt-6"}>{p}</p>
                </React.Fragment>
              );
            })}
          </div>

          <div className="md:clear-both pt-14 mt-8 border-t border-white/10 text-left md:text-right">
            {letter.signoff && (
              <p className="font-overline text-[0.7rem] text-stone-500 mb-2">{letter.signoff}</p>
            )}
            <p
              data-testid="director-signature"
              className="font-script text-5xl md:text-6xl text-[#DE9E48] leading-none italic"
              style={{ transform: "rotate(-3deg)", display: "inline-block" }}
            >
              {letter.signature_name}
            </p>
            <p className="font-overline text-[0.65rem] text-stone-400 mt-3">
              {letter.director_title} · Encore Theatre &amp; Gallery
            </p>
          </div>

          <div className="flex flex-wrap gap-4 justify-center md:justify-end mt-10">
            <Link to="/about" className="btn-outline" data-testid="home-about-link">
              Read Our Full Story <ArrowRight size={14} />
            </Link>
            <a
              href="https://encorebham.ludus.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold"
              data-testid="home-tickets-link"
            >
              <Ticket size={14} /> See What&apos;s On
            </a>
          </div>
        </div>
      </section>

      {/* SNAPSHOTS */}
      <section className="py-24 px-6 md:px-10 bg-stone-900/40">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              {snapshots.overline && (
                <p className="font-overline text-xs text-[#DE9E48] mb-4">— {snapshots.overline}</p>
              )}
              <h2 className="font-display text-5xl md:text-6xl tracking-tight">
                {snapshots.title_top} <span className="italic text-[#DE9E48]">{snapshots.title_italic}</span>
              </h2>
            </div>
            <Link to="/gallery" data-testid="snapshots-view-all" className="btn-outline">
              View All <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {gallery.map((g, i) => (
              <Link
                to="/gallery"
                key={g.id}
                data-testid={`snapshot-${i}`}
                className={`gallery-card aspect-square ${i === 0 ? "md:row-span-2 md:col-span-2 md:aspect-auto" : ""}`}
              >
                <img src={imageUrl(g.url)} alt={g.caption || `Encore moment ${i + 1}`} loading="lazy" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FROM THE STAGE — Editorial mosaic of past productions */}
      <section className="py-28 md:py-36 px-6 md:px-10 relative overflow-hidden">
        <PhotoWatermark index={4} position="right" opacity={0.12} />
        <div className="max-w-7xl mx-auto relative">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-16">
            <div className="max-w-2xl">
              <p className="font-overline text-[0.7rem] tracking-[0.3em] text-[#DE9E48] mb-4">
                — From the Stage
              </p>
              <h2 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.98]">
                A season of{" "}
                <span className="italic text-[#DE9E48]">moments.</span>
              </h2>
              <p className="font-body text-stone-300 mt-6 text-lg leading-relaxed">
                Songs, silences, standing ovations. A little of the theatre we
                keep making together — pulled from recent runs.
              </p>
            </div>
            <Link to="/gallery" className="btn-outline" data-testid="home-mosaic-view-all">
              Full Gallery <ArrowRight size={14} />
            </Link>
          </div>

          <PhotoMosaic photos={SHOW_PHOTOS.slice(0, 6)} />
        </div>
      </section>

      {/* ADDRESS BAND */}
      <section className="py-20 px-6 md:px-10 border-y border-white/10 bg-[#563727]/30">
        <div className="max-w-5xl mx-auto text-center">
          {addressBand.overline && (
            <p className="font-overline text-xs text-[#DE9E48] mb-3">— {addressBand.overline}</p>
          )}
          <h3 className="font-display text-3xl md:text-5xl tracking-tight">{addressBand.title}</h3>
          {addressBand.subtitle && (
            <p className="font-body text-stone-300 text-lg md:text-xl mt-2">{addressBand.subtitle}</p>
          )}
        </div>
      </section>

      {/* ARTICLES */}
      <section className="py-32 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14">
            {articlesSection.overline && (
              <p className="font-overline text-xs text-[#DE9E48] mb-4">— {articlesSection.overline}</p>
            )}
            <h2 className="font-display text-5xl md:text-6xl tracking-tight">
              {articlesSection.title_top}{" "}
              <span className="italic">{articlesSection.title_italic}</span>
            </h2>
            {articlesSection.subtitle && (
              <p className="font-body text-stone-400 mt-4 max-w-xl">{articlesSection.subtitle}</p>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {articles.map((a, i) => (
              <a
                key={a.id}
                href={a.link || "#"}
                target={a.link && a.link !== "#" ? "_blank" : "_self"}
                rel="noopener noreferrer"
                data-testid={`article-${i}`}
                className="group block border border-white/10 hover:border-[#DE9E48]/50 bg-stone-900/50 transition-colors"
              >
                {a.image_url && (
                  <div className="aspect-[16/9] overflow-hidden bg-stone-950">
                    <img
                      src={imageUrl(a.image_url)}
                      alt={a.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-6 md:p-8">
                  <h3 className="font-display text-2xl md:text-3xl leading-tight group-hover:text-[#DE9E48] transition-colors">
                    {a.title}
                  </h3>
                  <p className="font-body text-stone-400 mt-3 leading-relaxed">{a.excerpt}</p>
                  <p className="font-overline text-xs text-[#DE9E48] mt-6 flex items-center gap-2">
                    Read More <ArrowRight size={12} />
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
